/* iter150AG Part 20 — Telemetry diagnostics + self-heal panel.
 *
 * Reachable from the Live Dashboard header (yellow heartbeat dot) and
 * from the CP Config modal. Designed for support-engineer self-serve:
 * one click runs the deep-dive, second click optionally heals legacy
 * "serial-stored-as-device_id" rows.
 *
 * The panel never auto-runs — operators trigger it explicitly so we
 * don't add overhead to the page-load path.
 */
import { useEffect, useState } from "react";
import { CircleNotch, Stethoscope, CheckCircle, Warning, X,
         ArrowsCounterClockwise, Wrench, Recycle, Pause, Code } from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  seqTelemetryDiagnostics, seqHealDeviceIds,
  seqCpWebhookCaptureStart, seqCpWebhookCaptureStop, seqCpWebhookCaptureList,
  seqTelemetryProbe,
} from "@/lib/sequencingApi";


export default function TelemetryDiagnosticsPanel({ gid, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(false);
  const [captures, setCaptures] = useState({ items: [], active: false });
  const [captureBusy, setCaptureBusy] = useState(false);
  const [showCaptures, setShowCaptures] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [probe, setProbe] = useState(null);
  const [probeBusy, setProbeBusy] = useState(false);

  async function refreshCaptures() {
    try {
      const r = await seqCpWebhookCaptureList(50);
      setCaptures(r);
    } catch { /* tolerate */ }
  }

  // Poll captures list while capture-mode is active so the operator
  // sees rows accumulate in real time.
  useEffect(() => {
    if (!showCaptures) return;
    refreshCaptures();
    if (captures.active) {
      const id = setInterval(refreshCaptures, 2000);
      return () => clearInterval(id);
    }
  }, [showCaptures, captures.active]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function run() {
    setLoading(true);
    try {
      const r = await seqTelemetryDiagnostics(gid);
      setReport(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setLoading(false); }
  }

  async function heal() {
    if (!window.confirm(
      "Heal legacy device-IDs on this group? This translates any " +
      "members whose 'device_id' field holds a serial into the CP " +
      "UUID. Safe to re-run.")) return;
    setHealing(true);
    try {
      const r = await seqHealDeviceIds(gid);
      toast.success(
        `Healed ${r.members_fixed.length} member(s)` +
        (r.primary_fixed ? " + primary pressure device" : ""));
      // Re-run the diagnostic so the operator sees the result.
      await run();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setHealing(false); }
  }

  async function startCapture() {
    setCaptureBusy(true);
    try {
      await seqCpWebhookCaptureStart(20, 120);
      toast.success("Capturing the next 20 webhook payloads (auto-stop after 2 min).");
      setShowCaptures(true);
      await refreshCaptures();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setCaptureBusy(false); }
  }
  async function stopCapture() {
    setCaptureBusy(true);
    try {
      await seqCpWebhookCaptureStop();
      toast.success("Capture stopped.");
      await refreshCaptures();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setCaptureBusy(false); }
  }

  async function runProbe() {
    setProbeBusy(true);
    try {
      const r = await seqTelemetryProbe(gid);
      setProbe(r);
      if (!r.ok) {
        toast.error(r.reason || "no capture to probe");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message);
    } finally { setProbeBusy(false); }
  }

  return (
    <div data-testid="telemetry-diagnostics-panel"
         className="fixed inset-0 z-40 bg-slate-900/40 flex items-start justify-center pt-16">
      <div className="bg-white border border-slate-200 rounded-sm shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2">
          <Stethoscope size={18} className="text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800">
            Telemetry diagnostics
          </h2>
          {report && (
            <button type="button" onClick={run} disabled={loading}
                    data-testid="diag-rerun"
                    className="ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
              {loading ? <CircleNotch size={10} className="animate-spin" /> : <ArrowsCounterClockwise size={10} weight="bold" />}
              re-run
            </button>
          )}
          <button type="button" onClick={onClose}
                  data-testid="diag-close"
                  className={`${report ? "" : "ml-auto"} w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100`}>
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-4 text-xs">
          {!report ? (
            <div className="text-center py-12">
              <Stethoscope size={36} className="mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 mb-1">Trace why live telemetry isn't reaching this dashboard.</p>
              <p className="text-[10px] text-slate-400 mb-4">
                Aggregates webhook deliveries, point-name classification, group-member
                lookup health, and pressure-sample write-through for the last 5 minutes.
              </p>
              <button type="button" onClick={run} disabled={loading}
                      data-testid="diag-run"
                      className="text-[11px] font-bold uppercase px-3 py-2 rounded-sm bg-blue-700 text-white hover:bg-blue-800 inline-flex items-center gap-1.5 disabled:opacity-50">
                {loading ? <CircleNotch size={11} className="animate-spin" /> : <Stethoscope size={11} weight="bold" />}
                Run diagnostics
              </button>
            </div>
          ) : (
            <>
              {/* Diagnosis */}
              <section data-testid="diag-diagnosis"
                       className={`border rounded-sm p-3 ${
                         report.fixes_available?.length
                           ? "border-amber-300 bg-amber-50"
                           : report.members_in_telemetry_stream === report.members_total
                             ? "border-emerald-300 bg-emerald-50"
                             : "border-rose-300 bg-rose-50"}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  {report.members_in_telemetry_stream === report.members_total ? (
                    <CheckCircle size={14} weight="fill" className="text-emerald-700" />
                  ) : (
                    <Warning size={14} weight="fill" className="text-amber-700" />
                  )}
                  <h3 className="font-bold uppercase tracking-wider">Diagnosis</h3>
                </div>
                <ul className="space-y-1.5 list-disc pl-5">
                  {report.diagnosis.map((d, i) => (
                    <li key={i} className="text-slate-800 leading-snug">{d}</li>
                  ))}
                </ul>
                {report.fixes_available?.includes("heal_device_ids") && (
                  <button type="button" onClick={heal} disabled={healing}
                          data-testid="diag-heal-btn"
                          className="mt-3 text-[11px] font-bold uppercase px-3 py-1.5 rounded-sm bg-amber-700 text-white hover:bg-amber-800 inline-flex items-center gap-1.5 disabled:opacity-50">
                    {healing ? <CircleNotch size={11} className="animate-spin" /> : <Wrench size={11} weight="bold" />}
                    Heal legacy device-IDs
                  </button>
                )}
              </section>

              {/* iter150AG Part 24 — Probe latest capture (dry-run). */}
              <section data-testid="diag-probe-section"
                       className="border border-blue-300 bg-blue-50 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Stethoscope size={14} className="text-blue-700" />
                  <h3 className="font-bold uppercase tracking-wider text-blue-900">
                    Probe — replay latest capture
                  </h3>
                  <button type="button" onClick={runProbe} disabled={probeBusy}
                          data-testid="diag-probe-run"
                          className="ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1">
                    {probeBusy ? <CircleNotch size={10} className="animate-spin" /> : <Stethoscope size={10} weight="bold" />}
                    Run probe
                  </button>
                </div>
                <p className="text-[10px] text-blue-800 leading-snug">
                  Re-runs the matcher logic on the most recent captured payload (dry-run) and shows:
                  which payload keys were tried, which one matched a group member, every point
                  with its classifier verdict, and whether a pressure sample would be written.
                </p>
                {probe && probe.ok && (
                  <div data-testid="diag-probe-result"
                       className="mt-2 bg-white border border-blue-200 rounded-sm p-2 text-[11px] space-y-1.5">
                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                      <div>
                        <div className="text-slate-500 uppercase tracking-wider">Candidate keys</div>
                        <div className="font-mono text-slate-800">{probe.candidate_keys?.join(" · ") || "—"}</div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase tracking-wider">Matched key</div>
                        <div className={`font-mono font-bold ${probe.matched_keys?.length ? "text-emerald-700" : "text-rose-700"}`}>
                          {probe.matched_keys?.join(", ") || "NONE — no group member"}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase tracking-wider">Would write</div>
                        <div className={`font-bold ${probe.would_write_samples > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {probe.would_write_samples} pressure sample{probe.would_write_samples === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500 text-[10px] uppercase tracking-wider mb-0.5">Points + classifier verdict</div>
                      <table className="w-full text-[10px]">
                        <thead className="text-slate-400">
                          <tr><th className="text-left p-1">Name</th><th className="text-left p-1">→ Classified as</th><th className="text-right p-1">Value</th></tr>
                        </thead>
                        <tbody>
                          {probe.points.map((p, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="p-1 font-mono text-slate-700">{p.name}</td>
                              <td className={`p-1 font-mono ${p.classified_as ? "text-emerald-700 font-bold" : "text-rose-600"}`}>
                                {p.classified_as || "(unclassified)"}
                              </td>
                              <td className="p-1 text-right font-mono">{String(p.value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {probe && !probe.ok && (
                  <div data-testid="diag-probe-empty"
                       className="mt-2 text-[11px] text-rose-700 bg-white border border-rose-200 rounded-sm p-2">
                    {probe.reason}
                  </div>
                )}
              </section>

              {/* Counts */}
              <section className="grid grid-cols-5 gap-2">
                <CountTile label="Deliveries 5m" value={report.deliveries_last_5min} testid="diag-count-deliveries" />
                <CountTile label="Telemetry"     value={report.deliveries_last_5min_by_type?.["telemetry.updated"] || 0} testid="diag-count-telemetry" />
                <CountTile label="Pressure 5m"   value={report.pressure_samples_5m_group || 0} testid="diag-count-pressure"
                            tone={(report.pressure_samples_5m_group || 0) > 0 ? "emerald" : "slate"} />
                <CountTile label="In stream"     value={`${report.members_in_telemetry_stream}/${report.members_total}`} testid="diag-count-in-stream" />
                <CountTile label="Need heal"     value={report.members_need_heal} testid="diag-count-need-heal"
                            tone={report.members_need_heal > 0 ? "amber" : "slate"} />
              </section>

              {/* Per-member breakdown */}
              <section>
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Members ({report.members_total})
                </h3>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left p-2">device_id</th>
                        <th className="text-left p-2">Serial</th>
                        <th className="text-center p-2">In stream</th>
                        <th className="text-right p-2">Deliveries 5m</th>
                        <th className="text-right p-2">Samples 5m</th>
                        <th className="text-center p-2">Cache</th>
                        <th className="text-left p-2">Diagnosis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.members.map(m => (
                        <tr key={m.device_id}
                             data-testid={`diag-member-${m.device_id}`}
                             className="border-t border-slate-200 align-top">
                          <td className="p-2 font-mono text-slate-600 truncate max-w-[140px]">{m.device_id}</td>
                          <td className="p-2 text-slate-600">{m.serial_in_onboarded || "—"}</td>
                          <td className="p-2 text-center">
                            {m.in_telemetry_stream
                              ? <CheckCircle size={12} weight="fill" className="text-emerald-700 inline" />
                              : m.suggested_in_telemetry_stream
                                ? <span className="text-amber-700 text-[9px] font-bold">via {m.suggested_cp_device_id?.slice(0,8)}…</span>
                                : <X size={12} className="text-rose-600 inline" />}
                          </td>
                          <td className="p-2 text-right font-mono tabular-nums text-slate-700">
                            {m.deliveries_seen_5m || 0}
                          </td>
                          <td className="p-2 text-right font-mono tabular-nums">
                            {m.pressure_samples_5m + (m.pressure_samples_5m_for_suggested
                              ? ` (+${m.pressure_samples_5m_for_suggested})` : "")}
                          </td>
                          <td className="p-2 text-center">
                            {m.state_cache_present
                              ? <span className="text-emerald-700 text-[9px]" title={JSON.stringify(m.state_cache_values || {})}>
                                  {Math.round(m.state_cache_age_s || 0)}s
                                </span>
                              : <X size={12} className="text-slate-400 inline" />}
                          </td>
                          <td className="p-2 text-slate-700 leading-tight">
                            {m.deliveries_seen_5m > 0 ? (
                              <div>
                                <div className="text-emerald-700 font-bold">✓ matched, {m.deliveries_seen_5m} deliveries</div>
                                {m.classifier_breakdown && Object.keys(m.classifier_breakdown).length > 0 ? (
                                  <div className="text-[10px] text-slate-500 mt-0.5">
                                    classifier: {Object.entries(m.classifier_breakdown).map(([k,v]) => (
                                      <span key={k} className="font-mono mr-1.5">
                                        <b>{k.replace('current_','').replace('_status','')}</b>={v}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-[10px] text-amber-700 mt-0.5">
                                    no points classified — see names below
                                  </div>
                                )}
                                {m.sample_point_names?.length > 0 && (
                                  <div className="text-[9px] text-slate-400 mt-0.5 truncate" title={m.sample_point_names.join(", ")}>
                                    {m.sample_point_names.slice(0, 3).join(" · ")}
                                    {m.sample_point_names.length > 3 ? ` +${m.sample_point_names.length - 3}` : ""}
                                  </div>
                                )}
                              </div>
                            ) : m.looks_like_serial
                              ? <span className="text-amber-800">⚠ device_id looks like a serial — heal available</span>
                              : "Not seen in last-5m captures"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Point name classifier */}
              <section>
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Point names seen ({report.sample_point_names?.length || 0})
                </h3>
                {report.sample_point_names?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {report.sample_point_names.map(n => (
                      <span key={n} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700">
                        {n}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 italic text-[11px]">
                    No telemetry.updated payloads with non-slim points in the last 5 minutes.
                  </p>
                )}
                {report.point_classified_counts && Object.keys(report.point_classified_counts).length > 0 && (
                  <div className="mt-2 text-[10px] text-slate-500">
                    Classifier result:{" "}
                    {Object.entries(report.point_classified_counts).map(([k,v]) => (
                      <span key={k} className="font-mono mr-2">
                        <b>{k}</b>={v}
                      </span>
                    ))}
                  </div>
                )}
              </section>

              {/* CP-side device_ids */}
              {report.distinct_telemetry_device_ids?.length > 0 && (
                <section>
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    CP device_ids seen ({report.distinct_telemetry_device_ids.length})
                  </h3>
                  <div className="font-mono text-[10px] text-slate-600 space-y-0.5">
                    {report.distinct_telemetry_device_ids.slice(0, 10).map(d => (
                      <div key={d}>{d}</div>
                    ))}
                    {report.distinct_telemetry_device_ids.length > 10 && (
                      <div className="text-slate-400">… and {report.distinct_telemetry_device_ids.length - 10} more</div>
                    )}
                  </div>
                </section>
              )}

              {/* iter150AG Part 21 — Raw webhook payload capture. */}
              <section data-testid="diag-capture-section"
                       className="border-t border-slate-200 pt-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Code size={14} className="text-blue-700" />
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
                    Raw webhook captures
                  </h3>
                  {captures.active && (
                    <span data-testid="diag-capture-active"
                          className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-300">
                      capturing · {captures.remaining ?? 0} left
                    </span>
                  )}
                  <button type="button" onClick={() => { setShowCaptures(!showCaptures); refreshCaptures(); }}
                          data-testid="diag-capture-toggle-view"
                          className="ml-auto text-[10px] uppercase font-bold text-blue-700 hover:underline">
                    {showCaptures ? "hide" : "show buffer"}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-2">
                  Hot-path telemetry deliveries normally write a slim audit row to keep up with the ~10/s rate.
                  Toggle capture mode to record the next 20 FULL payloads (auto-stops after 2 min) — useful for
                  inspecting exactly what CP is sending.
                </p>
                <div className="flex items-center gap-1.5">
                  {captures.active ? (
                    <button type="button" onClick={stopCapture} disabled={captureBusy}
                            data-testid="diag-capture-stop"
                            className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1">
                      {captureBusy ? <CircleNotch size={10} className="animate-spin" /> : <Pause size={10} weight="fill" />}
                      Stop capture
                    </button>
                  ) : (
                    <button type="button" onClick={startCapture} disabled={captureBusy}
                            data-testid="diag-capture-start"
                            className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1">
                      {captureBusy ? <CircleNotch size={10} className="animate-spin" /> : <Recycle size={10} weight="fill" />}
                      Capture next 20
                    </button>
                  )}
                  <span className="text-[10px] text-slate-500">
                    Buffer: <b>{captures.total_in_buffer || captures.items.length}</b> rows
                  </span>
                </div>
                {showCaptures && (
                  <div data-testid="diag-capture-list"
                       className="mt-2 max-h-72 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100">
                    {captures.items.length === 0 ? (
                      <p className="p-3 text-[11px] italic text-slate-400 text-center">
                        Buffer empty. Click "Capture next 20" to start, then wait a few seconds for CP to deliver.
                      </p>
                    ) : captures.items.map((c, i) => {
                      const key = c.delivery_id || `${c.captured_at}-${i}`;
                      const open = expanded[key];
                      return (
                        <div key={key} data-testid={`diag-capture-row-${i}`} className="text-[11px]">
                          <button type="button" onClick={() => setExpanded({ ...expanded, [key]: !open })}
                                  className="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-center gap-2">
                            <span className="font-mono text-[9px] text-slate-400 w-24 shrink-0">
                              {new Date(c.captured_at).toLocaleTimeString()}
                            </span>
                            <span className="font-bold text-slate-700 w-32 shrink-0 truncate">
                              {c.event_type}
                            </span>
                            <span className="font-mono text-[10px] text-slate-500 truncate flex-1">
                              {c.payload?.device_id?.slice(0, 12) || ""}
                              {c.payload?.points && ` · ${c.payload.points.length} pts`}
                            </span>
                            <span className="text-slate-400 text-[10px]">{open ? "▾" : "▸"}</span>
                          </button>
                          {open && (
                            <pre data-testid={`diag-capture-payload-${i}`}
                                 className="bg-slate-900 text-emerald-200 text-[10px] p-2 overflow-x-auto font-mono leading-snug">
                              {JSON.stringify(c.payload, null, 2)}
                            </pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CountTile({ label, value, testid, tone = "slate" }) {
  const colorMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    amber: "bg-amber-50 border-amber-300 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-700",
  };
  return (
    <div data-testid={testid} className={`border rounded-sm p-2 text-center ${colorMap[tone]}`}>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-wider">{label}</div>
    </div>
  );
}
